import { useMemo } from 'react';

import { rem, Flex, Button } from '@vimeo/bokeh';

import {
  trackArchivePoll,
  trackOpenPoll,
  trackPublishPollResults,
} from '../../../../tracking/tracking_engagement_polls';
import {
  T_ARCHIVE_POLL,
  T_HIDE_FROM_SCENE,
  T_OPEN_POLL,
  T_PUBLISH_RESULTS,
  T_SHOW_ON_SCENE,
} from '../../../../translations';
import { EPollState, IPoll, IPollItemActions } from '../../../../types/interaction_tools';
import { createDomName } from '../../../../utils/dom/identifying';

interface IPollItemControlsProps {
  poll: IPoll;
  isPollShownInActiveScene?: boolean;
  isAnyPollPublished?: boolean;
  canOpenPoll: boolean;
  itemActions: IPollItemActions;
}

function getButtons({
  poll,
  canOpenPoll,
  onPollOpen,
  onPollClose,
  onToggleShowPoll,
  onPollArchive,
  isPollShownInActiveScene,
  isAnyPollPublished,
}: any) {
  return {
    open: (
      <Button
        key={'open'}
        className={createDomName('poll-item-action')}
        isDisabled={!canOpenPoll}
        size={'sm'}
        variant={'secondary'}
        onClick={(event) => {
          event.stopPropagation();
          onPollOpen(poll);

          if (isAnyPollPublished) {
            trackArchivePoll();
          }

          trackOpenPoll();
        }}
      >
        {T_OPEN_POLL}
      </Button>
    ),
    archive: (
      <Button
        key={'archive'}
        className={createDomName('poll-item-action')}
        variant={'secondary'}
        size={'sm'}
        onClick={(event) => {
          event.stopPropagation();
          onPollArchive(poll);
          trackArchivePoll();
        }}
      >
        {T_ARCHIVE_POLL}
      </Button>
    ),
    publish: (
      <Button
        key={'publish'}
        className={createDomName('poll-item-action')}
        size={'sm'}
        variant={'primary'}
        onClick={(event) => {
          event.stopPropagation();
          onPollClose(poll);
          trackPublishPollResults();
        }}
      >
        {T_PUBLISH_RESULTS}
      </Button>
    ),
    showResults: (
      <Button
        key={'show-results'}
        className={createDomName('poll-item-action')}
        size={'sm'}
        variant={isPollShownInActiveScene ? 'destructive' : 'secondary'}
        onClick={(event) => {
          event.stopPropagation();
          onToggleShowPoll && onToggleShowPoll(poll);
        }}
      >
        {isPollShownInActiveScene ? T_HIDE_FROM_SCENE : T_SHOW_ON_SCENE}
      </Button>
    ),
  };
}

export function PollItemActions({
  poll,
  isPollShownInActiveScene,
  isAnyPollPublished,
  canOpenPoll,
  itemActions: { onPollOpen, onPollArchive, onPollClose, onToggleShowPoll },
}: IPollItemControlsProps) {
  const buttons = useMemo(
    () =>
      getButtons({
        poll,
        canOpenPoll,
        onPollOpen,
        onPollArchive,
        onPollClose,
        onToggleShowPoll,
        isPollShownInActiveScene,
        isAnyPollPublished,
      }),
    [
      poll,
      canOpenPoll,
      onPollOpen,
      onPollArchive,
      onPollClose,
      onToggleShowPoll,
      isPollShownInActiveScene,
      isAnyPollPublished,
    ],
  );

  const showResultButton = onToggleShowPoll ? buttons.showResults : null;

  const actions = useMemo(() => {
    switch (poll.state) {
      case EPollState.CLOSED:
        return [buttons.archive, showResultButton];
      case EPollState.OPENED:
        return [buttons.publish, showResultButton];
      case EPollState.DRAFT:
        return [buttons.open, showResultButton];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll, buttons]);

  return poll.state !== EPollState.ARCHIVED ? (
    <Flex direction={'column'} gap={rem(8)}>
      {actions}
    </Flex>
  ) : null;
}
